import { z } from "zod";
import {
  mcpCredentialTransportSchema,
  mcpEndpointSchema,
  mcpHttpsUrlSchema,
  mcpIntegrationIdSchema,
  mcpIconSchema,
  mcpToolNameSchema,
} from "./copilot-mcp.js";

/**
 * What an AUTHORIZED ADMINISTRATOR may send about the global MCP catalog.
 *
 * Separated from `copilot-mcp.ts` on purpose, and the separation is the file's
 * main claim: everything in that file is either a runtime definition or one of
 * the three tiny things an ordinary user may submit, and nothing in it will
 * parse an endpoint, an auth strategy or a tool allowlist out of a request. The
 * schemas that WILL are all here, in a file whose every consumer is a route
 * mapped to `copilot.mcp_integrations_manage`. A reviewer asking "where can a
 * request change what Copilot may read from?" has exactly one file to read.
 *
 * ── Write-only secrets ──────────────────────────────────────────────────────
 *
 * `clientSecret` and `serviceAccountCredential` are the only fields in the
 * platform with three-state semantics, and the three states are the whole
 * reason the admin API can be honest about a value it will never show again:
 *
 *   field ABSENT   → leave whatever is stored alone. This is what an edit that
 *                    did not touch the secret sends, and it is the default, so
 *                    a form that renders "Configured ✓" and posts the rest of
 *                    the record cannot blank a working credential by omission.
 *   field = string → REPLACE. Encrypted before it reaches the database, audited
 *                    as "client secret replaced", and never echoed back.
 *   field = null   → CLEAR. An explicit act — switching an OAuth app from
 *                    confidential to public PKCE — and not something an empty
 *                    text input can do by accident, because the empty string is
 *                    refused by the min length rather than treated as null.
 *
 * There is no fourth state and, in particular, no way for a GET response to
 * feed back into a PATCH: the read shape carries `hasClientSecret: boolean`,
 * and a boolean cannot be replayed into a field that only accepts a secret.
 *
 * ── Why `.strict()` everywhere ──────────────────────────────────────────────
 *
 * A body with an unknown key is refused rather than trimmed. That converts a
 * whole class of mistake into a 400 at the moment it is made: a client that
 * still sends the old `clientIdEnv`, a wizard that posts `hasClientSecret`
 * back, an operator scripting against a field name they guessed. Silently
 * dropping any of those would produce a saved integration that differs from the
 * one the caller believes they saved.
 */

/**
 * A secret as it arrives ONCE.
 *
 * Bounded generously (providers issue long ones) and trimmed, because a
 * trailing newline pasted from a terminal is the single most common way a
 * correct secret is stored incorrectly. Not `.min(1)` after trimming by
 * accident — explicitly, so that "  " is a validation error and not a stored
 * empty credential that fails at 3am with an opaque provider message.
 */
const writeOnlySecretSchema = z
  .string()
  .trim()
  .min(1, { message: "Enter the secret, or omit the field to keep the current one." })
  .max(4_096);

/**
 * The admin-side auth strategy, carrying its secret exactly once.
 *
 * Mirrors `mcpIntegrationAuthSchema` field for field, plus the write-only
 * secret. It is deliberately a SEPARATE union rather than an extension: an
 * extension would mean the runtime definition type had a secret-shaped hole in
 * it that happened to be undefined, and the property worth having is that the
 * runtime type has no such field at all.
 *
 * `allowInsecureLocalhost` is threaded through because an administrator typing
 * a URL into a form deserves exactly the reading a deployment file got — no
 * more (production still refuses plain http) and no less (a developer running
 * an MCP server on their laptop can still wire it up).
 */
export function mcpAdminAuthSchema(
  options: { allowInsecureLocalhost?: boolean } = {},
) {
  return z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("none") }).strict(),

    z
      .object({
        kind: z.literal("service-account"),
        /**
         * Literal `true`, typed by whoever chose this mode. There is no
         * `service-account` that is not shared, and making the field
         * unavoidable is how the wizard's warning ends up being something the
         * administrator acknowledged rather than something they scrolled past.
         */
        shared: z.literal(true),
        transport: mcpCredentialTransportSchema.default({ kind: "bearer" }),
        /** Write-only. Absent = keep, string = replace, null = clear. */
        serviceAccountCredential: writeOnlySecretSchema.nullish(),
      })
      .strict(),

    z
      .object({
        kind: z.literal("token-user"),
        transport: mcpCredentialTransportSchema.default({ kind: "bearer" }),
        credentialLabel: z.string().trim().min(1).max(60).default("API token"),
        credentialHint: z.string().trim().max(300).nullish(),
      })
      .strict(),

    z
      .object({
        kind: z.literal("oauth2-user"),
        authorizationUrl: mcpHttpsUrlSchema({
          ...options,
          label: "OAuth authorization URL",
        }),
        tokenUrl: mcpHttpsUrlSchema({ ...options, label: "OAuth token URL" }),
        revocationUrl: mcpHttpsUrlSchema({
          ...options,
          label: "OAuth revocation URL",
        }).nullish(),
        /**
         * Optional, and the one field in this union whose optionality is the
         * point rather than a convenience.
         *
         * A client id has to exist before an authorization URL can be built,
         * and until discovery landed the only way to get one was for a person
         * to create an OAuth app in the provider's developer console and paste
         * it here. It is still accepted that way. But a provider that publishes
         * a `registrationEndpoint` will ISSUE one — RFC 7591 — and requiring
         * the field would mean refusing the configuration that needs the least
         * from the administrator.
         *
         * Exactly one of the two must be present, which the refinement below
         * enforces, and the API resolves the pair into a real client id before
         * anything is written: the RUNTIME definition still requires one, so
         * there is no stored integration with a hole where its client id goes.
         */
        clientId: z
          .string()
          .trim()
          .min(1)
          .max(400)
          .regex(/^\S+$/, { message: "An OAuth client id contains no spaces." })
          .optional(),
        /**
         * Where to ask for one, from RFC 8414 metadata. Supplied by discovery
         * and not typed — the wizard fills it in and shows the administrator
         * which host it names, which is the check that matters, because this is
         * the address a client registration request is sent to.
         */
        registrationEndpoint: mcpHttpsUrlSchema({
          ...options,
          label: "OAuth registration endpoint",
        }).nullish(),
        /** Write-only. Absent = keep, string = replace, null = clear (public PKCE client). */
        clientSecret: writeOnlySecretSchema.nullish(),
        scopes: z.array(z.string().trim().min(1).max(200)).max(40).default([]),
        pkce: z.boolean().default(true),
        audience: z.string().trim().max(200).nullish(),
        accountNameField: z.string().trim().max(120).nullish(),
        accountIdField: z.string().trim().max(120).nullish(),
        transport: mcpCredentialTransportSchema.default({ kind: "bearer" }),
      })
      .strict(),
  ]).superRefine((auth, ctx) => {
    // On the UNION rather than on the OAuth member, because Zod 3's
    // `discriminatedUnion` accepts only plain objects as branches — a member
    // carrying a refinement is refused at construction. The cost is one `kind`
    // check; the alternative, hoisting this into the create and update schemas,
    // would be two copies of the same rule that can drift apart.
    if (auth.kind !== "oauth2-user") return;
    if (auth.clientId || auth.registrationEndpoint) return;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["clientId"],
      message:
        "Enter a client ID, or run Detect settings so one can be registered automatically.",
    });
  });
}

export type McpAdminAuthInput = z.infer<ReturnType<typeof mcpAdminAuthSchema>>;

/**
 * "Ask this endpoint what its OAuth settings are."
 *
 * One field, and the field is the MCP endpoint the administrator has already
 * typed — not the authorization server, which is the value discovery exists to
 * find. Validated with the SAME endpoint schema the integration itself uses,
 * because this is the address the API is about to dial, and a discovery route
 * with a looser URL rule than the integration it feeds would be the way an
 * `http://` or credential-bearing URL got as far as a socket.
 *
 * `clientName` is what the deployment calls itself when a provider registers
 * an OAuth app for it. Optional, bounded, and display-only at the far end.
 */
export function mcpOauthDiscoverySchema(
  options: { allowInsecureLocalhost?: boolean } = {},
) {
  return z
    .object({
      endpoint: mcpEndpointSchema(options),
      clientName: z.string().trim().min(1).max(120).optional(),
    })
    .strict();
}

export type McpOauthDiscoveryInput = z.infer<ReturnType<typeof mcpOauthDiscoverySchema>>;

/**
 * The global tool allowlist, as the discovery screen submits it.
 *
 * A SET of names and nothing else — no descriptions, no annotations, no
 * "readOnly: true" the browser asserts. That is deliberate and it is the reason
 * this is its own endpoint rather than a field on the PATCH: the safety
 * classification of a tool is the SERVER's to state and ours to verify, live,
 * at the moment of the write. A body that could carry `readOnly` would be a
 * body through which an administrator could allowlist a destructive tool by
 * describing it as safe.
 */
export const mcpAllowedToolsSchema = z
  .object({
    tools: z
      .array(mcpToolNameSchema)
      .max(50)
      .transform((tools) => [...new Set(tools)]),
  })
  .strict();

export type McpAllowedToolsInput = z.infer<typeof mcpAllowedToolsSchema>;

/** The fields shared by create and edit. Split out so the two cannot drift. */
function integrationCoreShape(options: { allowInsecureLocalhost?: boolean }) {
  return {
    protocol: z.enum(["mcp", "text-webhook"]).default("mcp"),
    displayName: z.string().trim().min(1).max(80),
    description: z.string().trim().max(300).nullish(),
    icon: mcpIconSchema.nullish(),
    endpoint: mcpEndpointSchema(options),
    /**
     * Bounded here as well as in the column, because the ceiling is a product
     * decision rather than a storage one: an automatic tool call is spent
     * BEFORE the user's question is asked, so a 60-second integration is 60
     * seconds added to somebody's wait.
     */
    timeoutMs: z.coerce.number().int().min(1_000).max(60_000).default(15_000),
    readOnly: z.boolean().default(true),
    enabled: z.boolean().default(false),
  };
}

/**
 * Create an integration.
 *
 * `slug` is required and appears ONLY here — there is no slug on the update
 * schema, because it is the key every per-user connection and preference is
 * filed under and a rename would orphan all of them. `enabled` defaults FALSE:
 * the onboarding flow is create → connect → discover → allowlist → enable, and
 * a wizard whose first step could publish a source with no allowlist would be
 * offering users something that cannot answer.
 *
 * `allowedTools` is accepted at create for one reason — the legacy import, and
 * scripted provisioning, both of which already know the list. The GUI leaves it
 * empty and fills it from discovery.
 */
export function mcpIntegrationCreateSchema(
  options: { allowInsecureLocalhost?: boolean } = {},
) {
  return z
    .object({
      slug: mcpIntegrationIdSchema,
      ...integrationCoreShape(options),
      auth: mcpAdminAuthSchema(options),
      allowedTools: z
        .array(mcpToolNameSchema)
        .max(50)
        .default([])
        .transform((tools) => [...new Set(tools)]),
    })
    .strict()
    .superRefine((entry, ctx) => {
      if (entry.protocol !== "text-webhook") return;
      if (entry.auth.kind !== "none" && entry.auth.kind !== "service-account") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["auth", "kind"],
          message: "A text webhook supports no authentication or one shared header credential.",
        });
      }
      if (entry.auth.kind === "service-account" && entry.auth.transport.kind !== "header") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["auth", "transport"],
          message: "A text webhook credential must be sent in a custom header.",
        });
      }
    });
}

export type McpIntegrationCreateInput = z.infer<
  ReturnType<typeof mcpIntegrationCreateSchema>
>;

/**
 * Edit an integration.
 *
 * Every field optional, `version` required. The version is the
 * optimistic-concurrency token from the GET, and requiring it is not ceremony:
 * two administrators with the wizard open on the same integration is a
 * completely ordinary thing, and the failure mode without it — one of them
 * silently reverting the other's endpoint change — is invisible until a call
 * goes somewhere unexpected.
 *
 * `auth` is replaced WHOLESALE rather than patched. A half-applied auth
 * strategy is not a state worth being able to express: switching Monday from a
 * personal token to OAuth means naming the authorization URL, the token URL and
 * the client id together, and a partial patch that left `tokenUrl` pointing at
 * the previous provider is a bug that would only surface at the token request.
 * The secret fields inside it keep their own three-state semantics, so
 * replacing the auth block without re-typing the client secret is still one
 * request.
 */
export function mcpIntegrationUpdateSchema(
  options: { allowInsecureLocalhost?: boolean } = {},
) {
  const core = integrationCoreShape(options);
  return z
    .object({
      version: z.coerce.number().int().min(1),
      displayName: core.displayName.optional(),
      description: core.description,
      icon: core.icon,
      endpoint: core.endpoint.optional(),
      timeoutMs: core.timeoutMs.optional(),
      readOnly: z.boolean().optional(),
      enabled: z.boolean().optional(),
      auth: mcpAdminAuthSchema(options).optional(),
    })
    .strict();
}

export type McpIntegrationUpdateInput = z.infer<
  ReturnType<typeof mcpIntegrationUpdateSchema>
>;

/**
 * Delete an integration.
 *
 * The slug has to be typed back. A confirmation dialog is a UI affordance and
 * can be clicked through; requiring the id in the BODY means the API itself
 * refuses a delete that was aimed at the wrong row, which is the failure this
 * actually guards — an administrator with two tabs open, deleting the wrong
 * one, and taking a dozen people's connections with it.
 */
export const mcpIntegrationDeleteSchema = z
  .object({ confirmSlug: mcpIntegrationIdSchema })
  .strict();

export type McpIntegrationDeleteInput = z.infer<typeof mcpIntegrationDeleteSchema>;
